import React from "react";
import { Box, Chip, LinearProgress, Stack, Typography } from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { Row } from "../types";
import { formatTimeRange } from "../../../../utils/time";

// Shared display helpers for the Staff Class Capacity rows and mobile cards.
// Keeps formatting in one place so the desktop table and the mobile card view
// stay in sync.

export const AVATAR_PALETTE = [
  ["#dbeafe", "#1d4ed8"], ["#dcfce7", "#166534"], ["#fef3c7", "#92400e"],
  ["#f3e8ff", "#6b21a8"], ["#ffe4e6", "#9f1239"], ["#e0f2fe", "#075985"],
] as const;

export function stableAvatar(name: string) {
  const trimmed = (name || "?").trim();
  const initials = trimmed
    .split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "?";
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) hash = (hash * 31 + trimmed.charCodeAt(i)) >>> 0;
  const [bg, color] = AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
  return { initials, bg, color };
}

export const gradeLabel = (g: string) => {
  if (!g) return "";
  if (/^\d+$/.test(g)) return `Grade ${g}`;
  return g;
};

export const modeLabel = (m: string | null) => {
  if (!m) return "—";
  const k = m.toLowerCase();
  if (k === "offline" || k === "in_person") return "In Person";
  if (k === "online") return "Online";
  if (k === "hybrid") return "Hybrid";
  return m;
};

// Class name = clean derived label if the backend supplied one, otherwise
// fall back to the (rare) real class code, otherwise the muted #id fallback.
export function ClassCell({ row }: { row: Row }) {
  const primary = row.class_name || row.class_code;
  return (
    <Stack spacing={0.25}>
      {primary ? (
        <Typography variant="body2" fontWeight={600} color="#1b3a6b">
          {primary}
        </Typography>
      ) : (
        <Typography variant="body2" fontWeight={600} color="text.secondary">
          Class #{row.class_id}
        </Typography>
      )}
      {row.class_code && row.class_name && (
        <Typography variant="caption" color="text.secondary">{row.class_code}</Typography>
      )}
      {!row.class_code && primary && (
        <Typography variant="caption" color="text.secondary">Class #{row.class_id}</Typography>
      )}
    </Stack>
  );
}

// Grade badge + first three subject chips, "+N more" tooltip for the rest.
export function GradeSubjectCell({ row }: { row: Row }) {
  const grades = row.grades.slice(0, 3).map(gradeLabel).filter(Boolean);
  const gradesOverflow = Math.max(0, row.grades.length - 3);
  const subjects = row.subjects.slice(0, 3);
  const subjectsOverflow = Math.max(0, row.subjects.length - 3);
  const overflowTitle = row.subjects.slice(3).join(", ");

  return (
    <Stack spacing={0.5}>
      {grades.length > 0 && (
        <Box>
          {grades.map((g) => (
            <Chip key={g} size="small" variant="outlined" label={g}
              sx={{ mr: 0.5, mb: 0.25, borderColor: "#c7d2fe", color: "#1b3a6b", fontWeight: 600 }} />
          ))}
          {gradesOverflow > 0 && (
            <Chip size="small" variant="outlined" label={`+${gradesOverflow} more`}
              title={row.grades.slice(3).map(gradeLabel).join(", ")}
              sx={{ mr: 0.5, mb: 0.25, borderColor: "#e2e8f0", color: "#475569" }} />
          )}
        </Box>
      )}
      {subjects.length > 0 && (
        <Box>
          {subjects.map((s) => (
            <Chip key={s} size="small" label={s}
              sx={{ mr: 0.5, mb: 0.25, background: "#eff6ff", color: "#1b3a6b", fontWeight: 500 }} />
          ))}
          {subjectsOverflow > 0 && (
            <Chip size="small" variant="outlined" label={`+${subjectsOverflow} more`}
              title={overflowTitle}
              sx={{ mr: 0.5, mb: 0.25, borderColor: "#e2e8f0", color: "#475569" }} />
          )}
        </Box>
      )}
      {grades.length === 0 && subjects.length === 0 && (
        <Typography variant="caption" color="text.secondary">—</Typography>
      )}
    </Stack>
  );
}

export function ScheduleCell({ row }: { row: Row }) {
  const range = formatTimeRange(row.start_time, row.end_time);
  return (
    <Stack spacing={0.25}>
      <Typography variant="body2" fontWeight={600}>{row.day || "—"}</Typography>
      <Typography variant="caption" color="text.secondary">
        {range || "Time not set"}
      </Typography>
    </Stack>
  );
}

export function ModeLocationCell({ row }: { row: Row }) {
  return (
    <Stack spacing={0.5}>
      <Chip size="small" variant="outlined" label={modeLabel(row.study_mode)}
        sx={{ alignSelf: "flex-start", borderColor: "#e2e8f0" }} />
      <Stack direction="row" spacing={0.5} alignItems="center">
        <LocationOnOutlinedIcon fontSize="inherit" sx={{ color: "#64748b" }} />
        <Typography variant="body2">
          {row.location || <em style={{ color: "#94a3b8" }}>Not assigned</em>}
        </Typography>
      </Stack>
    </Stack>
  );
}

export function AvailableCell({ row }: { row: Row }) {
  if (row.capacity === null) {
    return <Chip size="small" variant="outlined" label="Unlimited"
      sx={{ borderColor: "#e2e8f0", color: "#475569" }} />;
  }
  const avail = row.available_seats ?? 0;
  const usage = row.capacity_usage ?? 0;
  const over = row.active_students > row.capacity;
  const color =
    over ? { bg: "#fee2e2", color: "#b91c1c" } :
    avail === 0 ? { bg: "#fef3c7", color: "#92400e" } :
    usage >= 70 ? { bg: "#fef3c7", color: "#92400e" } :
    { bg: "#dcfce7", color: "#166534" };
  const label = over ? `${row.active_students - row.capacity} over` : `${avail} open`;
  return <Chip size="small" label={label}
    sx={{ background: color.bg, color: color.color, fontWeight: 600 }} />;
}

export function UsageCell({ row }: { row: Row }) {
  if (row.capacity_usage === null) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  const pct = Math.round(row.capacity_usage);
  const clamped = Math.min(100, Math.max(0, pct));
  const barColor: "primary" | "warning" | "error" =
    pct > 100 ? "error" :
    pct >= 90 ? "warning" :
    pct >= 70 ? "warning" :
    "primary";
  return (
    <Box minWidth={90}>
      <Typography variant="body2" fontWeight={600}>{pct}%</Typography>
      <LinearProgress variant="determinate" value={clamped} color={barColor}
        sx={{ height: 6, borderRadius: 3, mt: 0.25 }} />
    </Box>
  );
}

export function StatusChip({ row }: { row: Row }) {
  const map: Record<Row["capacity_status"], { label: string; color: any }> = {
    available:   { label: "Available",       color: "success" },
    full:        { label: "Full",            color: "warning" },
    no_students: { label: "Empty",           color: "default" },
    unlimited:   { label: "Unlimited",       color: "info"    },
    no_capacity: { label: "Capacity Not Set", color: "default" },
    data_issue:  { label: "Data Issue",      color: "error"   },
  };
  // Escalate label when nearly full but not yet at cap.
  const usage = row.capacity_usage ?? 0;
  if (row.capacity_status === "available" && usage >= 90) {
    return <Chip size="small" color="warning" label="Almost Full" />;
  }
  const c = map[row.capacity_status] ?? { label: row.capacity_status, color: "default" };
  return <Chip size="small" color={c.color} label={c.label} />;
}
